/** Decorative-only budgets. No tier changes actors, physical terrain/obstacles,
 * targets, telegraphs, projectiles, sight clarity, simulation rate or resolution. */
export const SKY_DECORATIVE_QUALITY = Object.freeze([
  Object.freeze({ tier: 0, name: 'full', birds: 1, transports: 2, scenery: 1, cloudStride: 1 }),
  Object.freeze({
    tier: 1,
    name: 'balanced',
    birds: 0.5,
    transports: 1,
    scenery: 0.7,
    cloudStride: 2,
  }),
  Object.freeze({
    tier: 2,
    name: 'minimal',
    birds: 0,
    transports: 0,
    scenery: 0.45,
    cloudStride: 3,
  }),
]);

/** Learn only from visible, ready, active frames with advancing simulation time.
 * now is monotonic wall milliseconds; time is domain active seconds. A long gap
 * is not a slow gameplay frame. Pauses/loading reset evidence, retaining tier.
 * Sustained >24ms cadence reduces decoration after 3 seconds; <19ms recovers after 12 seconds.
 * Each change has 4 seconds of active cooldown, preventing rapid toggling or resume shocks. */
export function createSkyAdaptiveQuality() {
  let tier = 0,
    last = null,
    average = null,
    slow = 0,
    fast = 0,
    cooldown = 0,
    longFrames = 0;
  function clear() {
    last = null;
    average = null;
    slow = 0;
    fast = 0;
    longFrames = 0;
  }
  return {
    sample({ now, time, active }) {
      if (!active || !Number.isFinite(now) || !Number.isFinite(time)) {
        clear();
        return SKY_DECORATIVE_QUALITY[tier];
      }
      if (!last || time < last.time || now < last.now) {
        clear();
        last = { now, time };
        return SKY_DECORATIVE_QUALITY[tier];
      }
      if (time === last.time) return SKY_DECORATIVE_QUALITY[tier];
      const delta = now - last.now;
      last = { now, time };
      if (delta <= 0 || delta > 250) {
        average = null;
        slow = 0;
        fast = 0;
        // One scheduling hitch is ignored. Three consecutive 250–1000 ms
        // active frames are real severe cadence evidence, not a background gap.
        longFrames = delta > 250 && delta <= 1000 ? longFrames + 1 : 0;
        if (longFrames) cooldown = Math.max(0, cooldown - delta);
        if (longFrames >= 3 && tier < 2 && cooldown === 0) {
          tier++;
          cooldown = 4000;
          longFrames = 0;
        }
        return SKY_DECORATIVE_QUALITY[tier];
      }
      longFrames = 0;
      average =
        average === null ? delta : average + (delta - average) * (1 - Math.exp(-delta / 500));
      if (cooldown > 0) {
        cooldown = Math.max(0, cooldown - delta);
        slow = 0;
        fast = 0;
        return SKY_DECORATIVE_QUALITY[tier];
      }
      slow = average > 24 ? slow + delta : 0;
      fast = average < 19 ? fast + delta : 0;
      if ((slow >= 3000 && tier < 2) || (fast >= 12000 && tier > 0)) {
        tier += slow >= 3000 ? 1 : -1;
        slow = 0;
        fast = 0;
        cooldown = 4000;
      }
      return SKY_DECORATIVE_QUALITY[tier];
    },
    diagnostics: () => ({
      decorationTier: SKY_DECORATIVE_QUALITY[tier].name,
      activeFrameMs: average,
    }),
  };
}

/** Apply a budget only to registered noncolliding scenery batches. Keep tunnel
 * silhouettes intact; actual obstacle meshes are never members of these fields. */
export function applySkyDecorativeField(field, visible, decoration) {
  const count =
    field.key === 'tunnel'
      ? field.count
      : Math.min(field.count, Math.max(2, Math.ceil((field.count * decoration.scenery) / 2) * 2));
  for (const mesh of field.meshes) {
    mesh.visible = visible;
    mesh.count = count;
  }
  return count;
}
